import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserStore } from "../../../store/user";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Cabecalho, Carregando, NaoEncontrado, Pe } from "../componentes";
import { LinhaDeBuscaItem } from "../componentes/linha-de-busca";
import { NumeroAnimado } from "../componentes/numero-animado";
import { resumoDasPosicoes, ultimaMedicao, type LinhaDeBusca } from "../dados/buscas";
import { dataCurta } from "../dados/formatar";
import { useRelatorio } from "../hooks/use-relatorio";
import {
  Apoio,
  Bloco,
  Card,
  Centro,
  Chip,
  Chips,
  Etiqueta,
  Grade,
  Largura,
  Meta,
  Numero,
  Pagina,
  Pilula,
  Secao,
  cores,
} from "../styles";

const PAGINA = 20;

type Filtro = "TODAS" | "TOP3" | "TOP10" | "FORA" | "SEM_MEDICAO";

const FILTROS: { chave: Filtro; rotulo: string; aceita: (linha: LinhaDeBusca) => boolean }[] = [
  { chave: "TODAS", rotulo: "Todas", aceita: () => true },
  { chave: "TOP3", rotulo: "Entre as 3 primeiras", aceita: (linha) => linha.posicao >= 1 && linha.posicao <= 3 },
  { chave: "TOP10", rotulo: "Entre as 10 primeiras", aceita: (linha) => linha.posicao >= 1 && linha.posicao <= 10 },
  { chave: "FORA", rotulo: "Fora da lista", aceita: (linha) => linha.foraDaLista },
  { chave: "SEM_MEDICAO", rotulo: "Ainda sem medição", aceita: (linha) => linha.posicao < 1 && !linha.foraDaLista },
];

const Busca = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid ${cores.line};
  border-radius: 1000px;
  padding: 12px 18px;
  background: #ffffff;

  input {
    border: 0;
    outline: 0;
    flex: 1;
    font: inherit;
    font-size: 14px;
    color: ${cores.ink};
    background: transparent;
    min-width: 0;
  }
`;

const Coluna = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 16}px;
`;

const normalizar = (texto: string) => texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export const TodasAsBuscas = () => {
  const { profileId = "" } = useParams<{ profileId: string }>();
  // Cliente logado volta para a tela dele (com a troca de empresa), não para o link público.
  const cliente = useUserStore((estado) => estado.user?.userType === "CUSTOMER");
  const voltarPara = cliente ? `/home?perfil=${profileId}` : `/relatorio/${profileId}`;
  const { perfil, carregando, falhou } = useRelatorio(profileId, 30);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("TODAS");
  const [visiveis, setVisiveis] = useState(PAGINA);
  const hoje = useMemo(() => new Date(), []);

  const principais = useMemo(() => resumoDasPosicoes(perfil?.keywords, hoje), [perfil, hoje]);
  const extras = useMemo(() => resumoDasPosicoes(perfil?.extraKeywords, hoje), [perfil, hoje]);
  const todas = useMemo(() => [...principais.linhas, ...extras.linhas], [principais, extras]);
  const contagens = useMemo(
    () => Object.fromEntries(FILTROS.map((item) => [item.chave, todas.filter(item.aceita).length])) as Record<Filtro, number>,
    [todas],
  );
  const filtradas = useMemo(() => {
    const alvo = normalizar(busca);
    const aceita = FILTROS.find((item) => item.chave === filtro)?.aceita ?? (() => true);
    return todas.filter((linha) => aceita(linha) && (!alvo || normalizar(linha.titulo).includes(alvo)));
  }, [todas, busca, filtro]);

  if (carregando) {
    return (
      <Pagina>
        <Cabecalho voltar={{ href: voltarPara, rotulo: "Voltar ao relatório" }} />
        <Carregando />
      </Pagina>
    );
  }
  if (falhou || !perfil) {
    return (
      <Pagina>
        <Cabecalho />
        <NaoEncontrado />
      </Pagina>
    );
  }

  const medidas = principais.medidas + extras.medidas;
  const top3 = principais.top3 + extras.top3;
  const medicao = dataCurta(ultimaMedicao([...(perfil.keywords ?? []), ...(perfil.extraKeywords ?? [])]));
  const consultadoEm = dataCurta(hoje);

  return (
    <Pagina>
      <Cabecalho voltar={{ href: voltarPara, rotulo: "Voltar ao relatório" }} />

      <Largura>
        <Secao $atraso={0.15} style={{ paddingTop: 18, paddingBottom: 24 }}>
          <Centro>
            <Etiqueta>{perfil.profileName}</Etiqueta>
            <h1>Todas as buscas que trabalhamos para você</h1>
            <p className="frase" style={{ fontSize: 16, lineHeight: 1.6 }}>
              Cada busca mostra onde sua empresa aparece (Google Maps ou Pesquisa do Google) e a data em que a posição foi medida.
            </p>
            {medicao && <Meta>{`Medições GeoRanking · última em ${medicao}`}</Meta>}
          </Centro>
        </Secao>
      </Largura>

      <Largura>
        <Secao $atraso={0.3} style={{ paddingTop: 0, paddingBottom: 8 }}>
          <Bloco>
            <Card style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
              <Meta style={{ fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>Buscas acompanhadas</Meta>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <Numero $tamanho={56} $cor={cores.blue}>
                  <NumeroAnimado valor={todas.length} atraso={0.3} />
                </Numero>
                <span style={{ fontSize: 17, color: cores.ink2 }}>{`buscas · ${principais.total} principais e ${extras.total} complementares`}</span>
              </div>
            </Card>
            <Grade $colunas={2}>
              <Card style={{ display: "flex", flexDirection: "column", gap: 4, padding: "18px 20px" }}>
                <Numero $tamanho={34} $cor={cores.green}>
                  <NumeroAnimado valor={top3} atraso={0.4} />
                </Numero>
                <span style={{ fontSize: 13, fontFamily: fonts.gilroySemiBold }}>entre as 3 primeiras</span>
                <Meta>{`de ${medidas} com posição medida`}</Meta>
              </Card>
              <Card style={{ display: "flex", flexDirection: "column", gap: 4, padding: "18px 20px" }}>
                <Numero $tamanho={34}>
                  <NumeroAnimado valor={contagens.SEM_MEDICAO} atraso={0.5} />
                </Numero>
                <span style={{ fontSize: 13, fontFamily: fonts.gilroySemiBold }}>ainda sem medição</span>
                <Meta>{`${contagens.FORA} medidas e fora da lista`}</Meta>
              </Card>
            </Grade>
          </Bloco>
        </Secao>
      </Largura>

      <Largura>
        <Secao $atraso={0.45}>
          <Coluna>
            <Busca>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cores.ink4} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                type="search"
                placeholder="Buscar por palavra"
                aria-label="Buscar por palavra"
                value={busca}
                onChange={(evento) => {
                  setBusca(evento.target.value);
                  setVisiveis(PAGINA);
                }}
              />
            </Busca>
            <Chips role="tablist" aria-label="Situação da busca">
              {FILTROS.filter((item) => item.chave === "TODAS" || contagens[item.chave] > 0).map((item) => (
                <Chip
                  key={item.chave}
                  type="button"
                  role="tab"
                  aria-selected={filtro === item.chave}
                  $ativo={filtro === item.chave}
                  onClick={() => {
                    setFiltro(item.chave);
                    setVisiveis(PAGINA);
                  }}
                >
                  {`${item.rotulo} (${contagens[item.chave]})`}
                </Chip>
              ))}
            </Chips>
            <Card style={{ display: "flex", flexDirection: "column", padding: "8px 22px" }}>
              {filtradas.slice(0, visiveis).map((linha) => (
                <LinhaDeBuscaItem key={linha.id} linha={linha} />
              ))}
              {filtradas.length === 0 && (
                <Apoio style={{ margin: "14px 0" }}>Nenhuma busca encontrada com esse filtro.</Apoio>
              )}
            </Card>
            {filtradas.length > visiveis && (
              <Pilula as="button" type="button" $tipo="contorno" onClick={() => setVisiveis((valor) => valor + PAGINA)}>
                {`Mostrar mais (${filtradas.length - visiveis} restantes)`}
              </Pilula>
            )}
            <Meta>Posições medidas pela GeoRanking no Google Maps e na Pesquisa do Google. Medição com mais de 30 dias aparece como “medição antiga”.</Meta>
          </Coluna>
        </Secao>
      </Largura>

      <Pe texto={`Medições GeoRanking · consultado em ${consultadoEm}`} />
    </Pagina>
  );
};
